import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  FormControlLabel,
  useTheme,
  useMediaQuery,
  Stack,
  Alert,
  Chip,
  Tooltip,
  CircularProgress,
  alpha
} from '@mui/material';
import {
  Category as CategoryIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { fetchCategories, createCategory, updateCategory, deleteCategory } from '../categoriesApi';
import { useBranch } from '../BranchContext';
import { useTranslation } from 'react-i18next';

const Categories = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const { selectedBranch } = useBranch();
  const [open, setOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState({
    CategoryName: '',
    IsActive: true,
    CategoriesOrder: ''
  });
  const [isEdit, setIsEdit] = useState(false);

  useEffect(() => {
    loadCategories();
    // eslint-disable-next-line
  }, [selectedBranch]);

  const loadCategories = async () => {
    if (!selectedBranch) {
      setCategories([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchCategories(selectedBranch.id || selectedBranch.Id);
      const filtered = (data || []).filter(c => {
        const name = c.categoryName || c.CategoryName || '';
        return !name.startsWith("العروض:") &&
               !name.startsWith("مجموعة:") &&
               name !== "Offer" &&
               name !== "Deal";
      });
      setCategories(filtered);
    } catch (error) {
      console.error('Error loading categories:', error);
      Swal.fire(t('Error'), t('CategoryLoadFail'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (category = null) => {
    if (category) {
      setCurrentCategory({
        Id: category.id || category.Id,
        CategoryName: category.categoryName || category.CategoryName,
        IsActive: category.isActive !== undefined ? category.isActive : category.IsActive,
        CategoriesOrder: category.CategoriesOrder || category.categoriesOrder || '',
        BranchId: selectedBranch ? (selectedBranch.id || selectedBranch.Id) : ''
      });
      setIsEdit(true);
    } else {
      setCurrentCategory({
        CategoryName: '',
        IsActive: true,
        CategoriesOrder: '',
        BranchId: selectedBranch ? (selectedBranch.id || selectedBranch.Id) : ''
      });
      setIsEdit(false);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSave = async () => {
    if (!currentCategory.CategoryName) {
      Swal.fire(t('Error'), t('CategoryNameRequired'), 'error');
      return;
    }
    let payload = { ...currentCategory };
    if (payload.CategoriesOrder === '') payload.CategoriesOrder = null;
    else payload.CategoriesOrder = parseInt(payload.CategoriesOrder);
    if (selectedBranch) {
      payload.BranchId = selectedBranch.id || selectedBranch.Id;
    }
    try {
      if (isEdit) {
        await updateCategory(currentCategory.Id, payload);
        Swal.fire(t('Success'), t('CategoryUpdatedSuccessfully'), 'success');
      } else {
        await createCategory(payload);
        Swal.fire(t('Success'), t('CategoryCreatedSuccessfully'), 'success');
      }
      handleClose();
      loadCategories();
    } catch (error) {
      Swal.fire(t('Error'), t('CategorySaveFail'), 'error');
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: t('Are_You_Sure'),
      text: t('DeleteWarningCategory'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('Yes_Delete_It'),
      cancelButtonText: t('Cancel')
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteCategory(id);
          Swal.fire(t('Deleted'), t('CategoryDeletedSuccess'), 'success');
          loadCategories();
        } catch (error) {
          Swal.fire(t('Error'), t('CategoryDeleteFail'), 'error');
        }
      }
    });
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
      {/* ✅ Warning Alert - Mobile Optimized */}
      {!selectedBranch && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography 
            variant="h6" 
            sx={{ fontSize: { xs: '0.95rem', sm: '1.15rem' } }}
          >
            {t('SelectBranchWarning') || 'Please select a branch first'}
          </Typography>
        </Alert>
      )}

      <Card elevation={3} sx={{ borderRadius: { xs: 2, sm: 3 } }}>
        {/* ✅ Mobile-Optimized Card Header */}
        <CardHeader
          avatar={
            <CategoryIcon 
              color="primary" 
              sx={{ fontSize: { xs: 32, sm: 40 } }} 
            />
          }
          title={
            <Typography 
              variant="h5" 
              sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
            >
              {t('ManageCategories') || 'Manage Categories'}
            </Typography>
          }
          subheader={
            <Typography 
              variant="body2"
              sx={{ 
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                display: { xs: 'none', sm: 'block' }
              }}
            >
              {t('ManageCategoriesDesc') || 'Create, edit, and organize your product categories'}
            </Typography>
          }
          action={
            <Button 
              variant="contained" 
              startIcon={<AddIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />} 
              onClick={() => handleOpen()}
              disabled={!selectedBranch}
              size={isMobile ? "small" : "medium"}
              sx={{ 
                textTransform: 'none',
                fontWeight: 'bold',
                fontSize: { xs: '0.8rem', sm: '0.9rem' }
              }}
            >
              {isMobile ? t('AddCategory') : t('AddCategory')}
            </Button>
          }
          sx={{ 
            bgcolor: 'background.paper', 
            borderBottom: 1, 
            borderColor: 'divider',
            p: { xs: 2, sm: 2.5 },
            flexDirection: { xs: 'column', sm: 'row' },
            '& .MuiCardHeader-action': {
              alignSelf: { xs: 'flex-start', sm: 'center' },
              mt: { xs: 2, sm: 0 },
              width: { xs: '100%', sm: 'auto' }
            }
          }}
        />

        <CardContent sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={isMobile ? 40 : 50} />
            </Box>
          ) : (
            <>
              {/* ✅ Desktop: Table View */}
              {!isMobile && (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: { sm: '0.85rem', md: '0.9rem' } }}>
                          {t('ID') || 'ID'}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: { sm: '0.85rem', md: '0.9rem' } }}>
                          {t('CategoryName') || 'Category Name'}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: { sm: '0.85rem', md: '0.9rem' } }}>
                          {t('CategoriesOrder') || 'Order'}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: { sm: '0.85rem', md: '0.9rem' } }}>
                          {t('Status') || 'Status'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: { sm: '0.85rem', md: '0.9rem' } }}>
                          {t('Actions') || 'Actions'}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...categories].sort((a, b) => {
                        const orderA = a.CategoriesOrder ?? 9999;
                        const orderB = b.CategoriesOrder ?? 9999;
                        return orderA - orderB;
                      }).map((category) => (
                        <TableRow 
                          key={category.id || category.Id}
                          sx={{ 
                            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                            transition: 'background-color 0.2s'
                          }}
                        >
                          <TableCell sx={{ fontSize: { sm: '0.85rem', md: '0.9rem' } }}>
                            <Chip 
                              label={`#${category.id || category.Id}`} 
                              size="small" 
                              color="primary" 
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: { sm: '0.85rem', md: '0.9rem' }, fontWeight: 500 }}>
                            {category.categoryName || category.CategoryName}
                          </TableCell>
                          <TableCell sx={{ fontSize: { sm: '0.85rem', md: '0.9rem' } }}>
                            {category.CategoriesOrder ?? '-'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={category.isActive || category.IsActive ? <CheckCircleIcon /> : <CancelIcon />}
                              label={category.isActive || category.IsActive ? t('Active') : t('Inactive')}
                              color={category.isActive || category.IsActive ? 'success' : 'error'}
                              size="small"
                              sx={{ fontSize: { sm: '0.75rem', md: '0.8rem' } }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title={t('Edit') || 'Edit'} arrow>
                              <IconButton 
                                color="primary" 
                                onClick={() => handleOpen(category)}
                                size="small"
                              >
                                <EditIcon sx={{ fontSize: { sm: 18, md: 20 } }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('Delete') || 'Delete'} arrow>
                              <IconButton 
                                color="error" 
                                onClick={() => handleDelete(category.id || category.Id)}
                                size="small"
                              >
                                <DeleteIcon sx={{ fontSize: { sm: 18, md: 20 } }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                      {categories.length === 0 && !loading && (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                            <CategoryIcon sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
                            <Typography color="text.secondary">
                              {t('NoCategoriesFound') || 'No categories found'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* ✅ Mobile: Card View */}
              {isMobile && (
                <Stack spacing={2}>
                  {[...categories].sort((a, b) => {
                    const orderA = a.CategoriesOrder ?? 9999;
                    const orderB = b.CategoriesOrder ?? 9999;
                    return orderA - orderB;
                  }).map((category) => (
                    <Paper 
                      key={category.id || category.Id}
                      elevation={2}
                      sx={{ 
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        transition: 'all 0.3s',
                        '&:active': {
                          transform: 'scale(0.98)',
                          boxShadow: 1
                        }
                      }}
                    >
                      <Stack spacing={1.5}>
                        {/* Header Row */}
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip 
                              label={`#${category.id || category.Id}`} 
                              size="small" 
                              color="primary" 
                              sx={{ fontSize: '0.7rem', height: 22 }}
                            />
                            <Chip
                              icon={category.isActive || category.IsActive ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : <CancelIcon sx={{ fontSize: 14 }} />}
                              label={category.isActive || category.IsActive ? t('Active') : t('Inactive')}
                              color={category.isActive || category.IsActive ? 'success' : 'error'}
                              size="small"
                              sx={{ fontSize: '0.7rem', height: 22 }}
                            />
                          </Stack>
                          <Stack direction="row" spacing={0.5}>
                            <IconButton 
                              color="primary" 
                              onClick={() => handleOpen(category)}
                              size="small"
                            >
                              <EditIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                            <IconButton 
                              color="error" 
                              onClick={() => handleDelete(category.id || category.Id)}
                              size="small"
                            >
                              <DeleteIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                          </Stack>
                        </Stack>

                        {/* Category Name */}
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontSize: '1rem', 
                            fontWeight: 600,
                            color: 'text.primary'
                          }}
                        >
                          {category.categoryName || category.CategoryName}
                        </Typography>

                        {/* Order Info */}
                        {category.CategoriesOrder && (
                          <Box sx={{ 
                            bgcolor: alpha(theme.palette.info.main, 0.08),
                            p: 1,
                            borderRadius: 1,
                            display: 'inline-block',
                            alignSelf: 'flex-start'
                          }}>
                            <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'info.main', fontWeight: 500 }}>
                              {t('Order') || 'Order'}: {category.CategoriesOrder}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </Paper>
                  ))}

                  {categories.length === 0 && !loading && (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <CategoryIcon sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
                      <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                        {t('NoCategoriesFound') || 'No categories found'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {t('ClickAddToCreateCategory') || 'Click "Add" to create your first category'}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ✅ Mobile-Optimized Dialog */}
      <Dialog 
        open={open} 
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'flex-start',
            paddingTop: { xs: '100px', sm: '80px', md: '100px' }
          }
        }}
        PaperProps={{
          sx: { 
            borderRadius: isMobile ? 0 : 2,
            maxHeight: { xs: 'calc(100vh - 200px)', sm: 'calc(100vh - 160px)', md: 'calc(100vh - 200px)' },
            overflowY: 'auto'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'white',
          p: { xs: 2, sm: 2.5 }
        }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CategoryIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              {isEdit ? (t('EditCategory') || 'Edit Category') : (t('AddCategory') || 'Add Category')}
            </Typography>
          </Stack>
        </DialogTitle>
        
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={{ xs: 2, sm: 2.5 }} sx={{ mt: 1 }}>
            <Box>
              <Typography 
                variant="subtitle2" 
                gutterBottom 
                fontWeight="bold"
                sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
              >
                {t('CategoryName') || 'Category Name'} *
              </Typography>
              <TextField
                fullWidth
                value={currentCategory.CategoryName}
                onChange={(e) => setCurrentCategory({ ...currentCategory, CategoryName: e.target.value })}
                placeholder={t('EnterCategoryName') || 'Enter category name'}
                size={isMobile ? "small" : "medium"}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.9rem', sm: '1rem' }
                  }
                }}
              />
            </Box>

            <Box>
              <Typography 
                variant="subtitle2" 
                gutterBottom 
                fontWeight="bold"
                sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
              >
                {t('CategoriesOrder') || 'Display Order'}
              </Typography>
              <TextField
                fullWidth
                value={currentCategory.CategoriesOrder}
                onChange={(e) => setCurrentCategory({ ...currentCategory, CategoriesOrder: e.target.value.replace(/[^0-9]/g, '') })}
                type="number"
                inputProps={{ min: 1 }}
                placeholder={t('EnterOrder') || 'Enter display order'}
                helperText={t('categoriesOrderHelper') || 'Lower numbers appear first (optional)'}
                size={isMobile ? "small" : "medium"}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.9rem', sm: '1rem' }
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: { xs: '0.7rem', sm: '0.75rem' }
                  }
                }}
              />
            </Box>

            <Paper 
              elevation={0}
              sx={{ 
                p: { xs: 1.5, sm: 2 }, 
                bgcolor: alpha(theme.palette.success.main, 0.05),
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={currentCategory.IsActive}
                    onChange={(e) => setCurrentCategory({ ...currentCategory, IsActive: e.target.checked })}
                    size={isMobile ? "small" : "medium"}
                  />
                }
                label={
                  <Typography sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                    {t('Is_Active') || 'Category is Active'}
                  </Typography>
                }
              />
            </Paper>
          </Stack>
        </DialogContent>
        
        <DialogActions sx={{ 
          p: { xs: 2, sm: 2.5 }, 
          bgcolor: '#f9f9f9',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 }
        }}>
          <Button 
            onClick={handleClose}
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
            sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
          >
            {t('Cancel') || 'Cancel'}
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
            sx={{ 
              fontSize: { xs: '0.85rem', sm: '0.9rem' },
              boxShadow: 3,
              '&:hover': {
                boxShadow: 6
              }
            }}
          >
            {isEdit ? (t('Update') || 'Update') : (t('Create') || 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Categories;